import { type IntelOrgMemberDetailDto, IntelligencePersona } from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { ConfirmDeleteModal } from '@components/shared/confirm-delete-modal.component';
import { Tile } from '@components/shared/tile.component';
import { ApiErrorWrapper } from '@hooks/api.hook';
import { errorToast, successToast } from '@hooks/toast.hook';
import {
  useIntelOrganizationMembersQuery,
  useRemoveIntelOrganizationMember,
  useSetIntelOrganizationMemberPersona,
} from '@queries/intel-organization.query';
import { useGetCurrentUser } from '@queries/users.query';
import { PlusIcon } from 'lucide-react';
import { type FC, useState } from 'react';
import { IntelTeamAddMemberModal } from './intel-team-add-member-modal.component';
import { IntelTeamMemberRow } from './intel-team-member-row.component';

export const IntelTeamDetailsTile: FC = () => {
  const { data: user } = useGetCurrentUser();
  const { data: members, isLoading } = useIntelOrganizationMembersQuery(true);

  const { mutateAsync: setPersona } = useSetIntelOrganizationMemberPersona();
  const { mutateAsync: removeMember } = useRemoveIntelOrganizationMember();

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<IntelOrgMemberDetailDto | null>(null);
  const [updatingMemberId, setUpdatingMemberId] = useState<string | null>(null);

  // The members list mirrors every user_company row of this org, including the current user,
  // so the current user's persona on the selected org is the persona on their own member row.
  const currentMember = user ? members?.find((member) => member.userId === user.id) : undefined;
  const isProgramAdmin = currentMember?.persona === IntelligencePersona.PROGRAM_ADMIN;

  const handlePersonaChange = async (member: IntelOrgMemberDetailDto, persona: IntelligencePersona): Promise<void> => {
    if (member.persona === persona) return;

    setUpdatingMemberId(member.id);
    try {
      await setPersona({ data: { persona }, userCompanyId: member.id });
      successToast({ description: 'Member persona updated.' });
    } catch (error) {
      const message =
        error instanceof ApiErrorWrapper ? error.message : 'Unable to update persona. Please try again later.';
      errorToast({ description: message });
    } finally {
      setUpdatingMemberId(null);
    }
  };

  const handleRemoveConfirm = async (): Promise<void> => {
    if (!memberToRemove) return;
    try {
      await removeMember(memberToRemove.id);
      successToast({ description: 'Member removed.' });
    } catch (error) {
      const message =
        error instanceof ApiErrorWrapper ? error.message : 'Unable to remove member. Please try again later.';
      errorToast({ description: message });
    } finally {
      setMemberToRemove(null);
    }
  };

  return (
    <Tile
      emptyState={{ title: 'No Team Members' }}
      hoverReveal
      isEmpty={!isLoading && !members?.length}
      isLoading={isLoading}
    >
      <div className="flex flex-col gap-4">
        <div className="flex w-full flex-row flex-wrap items-center justify-between">
          <div className="text-[16px] font-medium text-[#1F1C1AE5]">Members</div>
          {isProgramAdmin && (
            <IconButton.HoverReveal
              ariaLabel="Add Team Member"
              icon={<PlusIcon />}
              onClick={() => setIsAddModalOpen(true)}
              variant="secondary"
            />
          )}
        </div>

        <div className="flex flex-col gap-2">
          {members?.map((member) => (
            <IntelTeamMemberRow
              key={member.id}
              isProgramAdmin={isProgramAdmin}
              isUpdating={updatingMemberId === member.id}
              member={member}
              onPersonaChange={(persona) => void handlePersonaChange(member, persona)}
              onRemove={() => setMemberToRemove(member)}
            />
          ))}
        </div>
      </div>

      {isProgramAdmin && (
        <IntelTeamAddMemberModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
        />
      )}

      <ConfirmDeleteModal
        isOpen={Boolean(memberToRemove)}
        itemName={`team member ${memberToRemove?.fullName || memberToRemove?.email || ''}`.trim()}
        onConfirm={handleRemoveConfirm}
        setIsOpen={(open) => {
          if (!open) setMemberToRemove(null);
        }}
      />
    </Tile>
  );
};
